import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Dumbbell,
  BarChart3,
  Calendar,
  Clock,
  Grid3x3,
  Trophy,
  MessageSquare,
  Settings,
  Salad,
  Activity,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useRole } from '../lib/role';
import styles from './ProSidebar.module.css';

interface NavItemDef {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NUTRI_NAV: NavItemDef[] = [
  { to: '/dashboard', label: 'Panoramica', icon: LayoutDashboard },
  { to: '/clienti', label: 'Pazienti', icon: Users },
  { to: '/calendario', label: 'Agenda', icon: Calendar },
  { to: '/piani', label: 'Piani alimentari', icon: ClipboardList },
  { to: '/catalogo', label: 'Catalogo cibi', icon: Salad },
];

const COACH_NAV: NavItemDef[] = [
  { to: '/dashboard', label: 'Panoramica', icon: LayoutDashboard },
  { to: '/atleti', label: 'Atleti', icon: Users },
  { to: '/calendario', label: 'Agenda', icon: Calendar },
  { to: '/sessioni-live', label: 'Sessioni live', icon: Clock },
  { to: '/programmi', label: 'Programmi', icon: Grid3x3 },
  { to: '/esercizi', label: 'Esercizi', icon: Dumbbell },
  { to: '/pr', label: 'PR & record', icon: Trophy },
];

const SHARED_NAV: NavItemDef[] = [
  { to: '/analytics', label: 'Analisi', icon: BarChart3 },
];

const SYSTEM_NAV: NavItemDef[] = [
  { to: '/messaggi', label: 'Messaggi', icon: MessageSquare },
  { to: '/impostazioni', label: 'Impostazioni', icon: Settings },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ProSidebar() {
  const { user } = useAuth();
  const { role, setRole, canSwitch } = useRole();

  const studioName = user?.studio_name || 'Studio Pro';
  const navItems = role === 'coach' ? COACH_NAV : NUTRI_NAV;
  const userRoleLabel =
    role === 'coach' ? 'Personal Trainer · Pro' : 'Nutrizionista · Pro';

  return (
    <aside className={styles.sidebar} aria-label="Navigazione principale">
      <div className={styles.brand}>
        <svg className={styles.brandMark} viewBox="0 0 80 80" aria-hidden>
          <rect x="0" y="0" width="80" height="80" rx="20" fill="#0b8a3e" />
          <path d="M18 14 H42 a26 26 0 0 1 0 52 H18 Z" fill="#fff" />
          <path d="M28 22 H40 a18 18 0 0 1 0 36 H28 Z" fill="#0b8a3e" />
          <circle cx="62" cy="22" r="6" fill="#ff7a1a" />
        </svg>
        <div className={styles.brandName}>
          dieta<em>menu</em><b>.</b>
        </div>
        <span className={styles.brandTag}>PRO</span>
      </div>

      {canSwitch && (
        <div className={styles.roleSwitch} role="tablist" aria-label="Cambia ruolo">
          <button
            type="button"
            role="tab"
            aria-selected={role === 'nutri'}
            className={`${styles.roleBtn} ${role === 'nutri' ? styles.roleBtnActiveNutri : ''}`}
            onClick={() => setRole('nutri')}
          >
            <Salad size={14} strokeWidth={2} />
            Nutri
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={role === 'coach'}
            className={`${styles.roleBtn} ${role === 'coach' ? styles.roleBtnActiveCoach : ''}`}
            onClick={() => setRole('coach')}
          >
            <Dumbbell size={14} strokeWidth={2} />
            Coach
          </button>
        </div>
      )}

      <div className={styles.navSection}>Studio</div>
      <nav className={styles.nav}>
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
            }
          >
            <Icon size={18} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
        {SHARED_NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
            }
          >
            <Icon size={18} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className={styles.navSection}>Sistema</div>
      <nav className={styles.nav}>
        {SYSTEM_NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
            }
          >
            <Icon size={18} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {user && (
        <div className={styles.sidebarFooter}>
          <div className={styles.userCard}>
            <div className={styles.userAvatar} aria-hidden>
              {initials(user.name)}
            </div>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userRole}>{userRoleLabel}</div>
            </div>
            <Activity size={14} strokeWidth={1.8} style={{ color: 'rgba(255,255,255,.5)' }} />
          </div>
          <div style={{ padding: '4px 6px 0', fontSize: 10, color: 'rgba(255,255,255,.35)', fontFamily: 'var(--mono)', letterSpacing: 0.5 }}>
            {studioName}
          </div>
        </div>
      )}
    </aside>
  );
}
